import { useTheme } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";

export const createClassName = (name: string) => {
  return {
    toString() {
      return name;
    },
    toClassName() {
      return `.${name}`;
    },
  };
};

export const mixDiffCellClassName = createClassName("mix-diff-cell");
export const mixDiffHeaderClassName = createClassName("mix-diff-header");
export const mixSummaryCellClassName = createClassName("mix-summary-cell");
export const mixSummaryHeaderClassName = createClassName("mix-summary-header");
export const numHeatsCellClassName = createClassName("num-heats-cell");
export const numHeatsHeaderClassName = createClassName("num-heats-header");
export const copperGroupCellClassName = createClassName("copper-group-cell");
export const copperGroupHeaderClassName = createClassName(
  "copper-group-header"
);
export const subStandardGroupCellClassName = createClassName(
  "sub-standard-group-cell"
);
export const subStandardGroupHeaderClassName = createClassName(
  "sub-standard-group-header"
);
export const driWeightCellClassName = createClassName("dri-weight-cell");
export const driWeightHeaderClassName = createClassName("dri-weight-header");
export const materialTotalCellClassName = createClassName(
  "material-total-cell"
);
export const materialWeightCellClassName = createClassName(
  "material-weight-cell"
);
export const materialHeaderClassName = createClassName("material-header");
export const basketWeightHeaderClassName = createClassName(
  "basket-weight-header"
);
export const basketWeightTotalCellClassName = createClassName(
  "basket-weight-total-cell"
);
export const basketWeightEmptyCellClassName = createClassName(
  "basket-weight-empty-cell"
);
export const basketWeightUsedCellClassName = createClassName(
  "basket-weight-used-cell"
);
export const warnedSteelGradesCellClassName = createClassName(
  "warned-steel-grades-cell"
);
export const warnedSteelGradesHeaderClassName = createClassName(
  "warned-steel-grades-header"
);
export const popoverSteelGradesCellClassName = createClassName(
  "popover-steel-grades-cell"
);
export const popoverSteelGradesHeaderClassName = createClassName(
  "popover-steel-grades-header"
);
export const supervisorSteelGradesCellClassName = createClassName(
  "supervisor-steel-grades-cell"
);
export const supervisorSteelGradesHeaderClassName = createClassName(
  "supervisor-steel-grades-header"
);
export const grabTypeHeaderClassName = createClassName("grab-type-header");
export const nameOverrideCellClassName = createClassName("name-override-cell");
export const nameOverrideHeaderClassName = createClassName(
  "name-override-header"
);
export const emptyGroupColumnHeaderClassName = createClassName(
  "empty-group-column-header"
);
export const bottomEmptyGroupColumnHeaderClassName = createClassName(
  "bottom-empty-group-column-header"
);
export const groupColumnRowHeaderClassName = createClassName(
  "group-column-row-header"
);
export const bottomGroupColumnRowHeaderClassName = createClassName(
  "bottom-group-column-row-header"
);
export const groupColumnCellClassName = createClassName("group-column-cell");
export const groupColumnHeaderClassName = createClassName(
  "group-column-header"
);
export const groupLeftColumnHeaderClassName = createClassName(
  "group-left-column-header"
);

export const useSx = (): SystemStyleObject<Theme> => {
  const theme = useTheme();

  return {
    width: "100%",
    [mixDiffCellClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
    },
    [mixDiffHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [mixSummaryCellClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
    },
    [mixSummaryHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [numHeatsCellClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
      display: "grid",
      justifyContent: "center",
      alignContent: "center",
    },
    [numHeatsHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [copperGroupCellClassName.toClassName()]: {
      backgroundColor: `${theme.palette.warning.light} !important`,
      display: "grid",
      justifyContent: "center",
      alignContent: "center",
    },
    [copperGroupHeaderClassName.toClassName()]: {
      backgroundColor: `${theme.palette.warning.light} !important`,
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [subStandardGroupHeaderClassName.toClassName()]: {
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [subStandardGroupCellClassName.toClassName()]: {
      backgroundColor: `${theme.palette.grey[50]} !important`,
      fontFamily: `${
        theme.typography.body2.fontFamily ?? "sans-serif"
      } !important`,
      fontWeight: `${String(theme.typography.fontWeightBold)} !important`,
      display: "flex",
      alignItems: "center",
      textWrap: "wrap",
    },
    [driWeightCellClassName.toClassName()]: {
      backgroundColor: `${theme.palette.grey[200]} !important`,
      display: "grid",
      justifyContent: "center",
      alignContent: "center",
    },
    [driWeightHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[200],
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [materialHeaderClassName.toClassName()]: {
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [materialTotalCellClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
      display: "grid",
      justifyContent: "center",
      alignContent: "center",
    },
    [materialWeightCellClassName.toClassName()]: {
      display: "grid",
      justifyContent: "center",
      alignContent: "center",
    },
    [basketWeightHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[200],
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
      borderRight: `1px solid ${theme.palette.secondary.dark}`,
    },
    [basketWeightTotalCellClassName.toClassName()]: {
      backgroundColor: `${theme.palette.grey[200]} !important`,
      borderRight: `1px solid ${theme.palette.secondary.dark}`,
      alignContent: "center",
    },
    [basketWeightEmptyCellClassName.toClassName()]: {
      backgroundColor: `${theme.palette.grey[200]} !important`,
      borderRight: `1px solid ${theme.palette.secondary.dark}`,
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center",
    },
    [basketWeightUsedCellClassName.toClassName()]: {
      backgroundColor: `${theme.palette.grey[200]} !important`,
      borderRight: `1px solid ${theme.palette.secondary.dark}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    [warnedSteelGradesCellClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
    },
    [warnedSteelGradesHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [popoverSteelGradesCellClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
    },
    [popoverSteelGradesHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [supervisorSteelGradesCellClassName.toClassName()]: {
      backgroundColor: `${theme.palette.grey[50]} !important`,
    },
    [supervisorSteelGradesHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [grabTypeHeaderClassName.toClassName()]: {
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [nameOverrideCellClassName.toClassName()]: {
      backgroundColor: theme.palette.common.white,
      display: "flex",
      alignItems: "center",
    },
    [nameOverrideHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.common.white,
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
      [".MuiDataGrid-columnHeaderTitle"]: {
        whiteSpace: "normal",
        lineHeight: 1.2,
      },
    },
    [emptyGroupColumnHeaderClassName.toClassName()]: {
      visibility: "hidden",
      height: 0,
      minHeight: "0 !important",
      maxHeight: "0 !important",
      padding: "0 !important",
      overflow: "hidden",
    },
    [bottomEmptyGroupColumnHeaderClassName.toClassName()]: {
      background: "none",
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
    },
    [groupColumnRowHeaderClassName.toClassName()]: {
      borderRight: `1px solid ${theme.palette.secondary.dark}`,
      [".MuiDataGrid-columnHeaderTitleContainer"]: {
        width: "100%",
        display: "flex",
        justifyContent: "center",
      },
    },
    [bottomGroupColumnRowHeaderClassName.toClassName()]: {
      borderRight: `1px solid ${theme.palette.secondary.dark}`,
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
      [".MuiDataGrid-columnHeaderTitleContainer"]: {
        width: "100%",
        display: "flex",
        justifyContent: "center",
      },
    },
    [groupColumnCellClassName.toClassName()]: {
      backgroundColor: theme.palette.common.white,
      borderBottom: `1px solid ${theme.palette.secondary.dark} !important`,
      fontFamily: theme.typography.body1Mono.fontFamily,
      width: "100%",
      [".MuiDataGrid-columnHeaderTitleContainer"]: {
        width: "100%",
        [".MuiDataGrid-columnHeaderTitleContainerContent"]: {
          width: "100%",
          height: "100%",
        },
      },
    },
    [groupColumnHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.common.white,
      fontFamily: theme.typography.body1Mono.fontFamily,
      fontWeight: `${String(theme.typography.fontWeightBold)} !important`,
      width: "100%",
      [".MuiDataGrid-columnHeaderTitleContainer"]: {
        width: "100%",
        [".MuiDataGrid-columnHeaderTitleContainerContent"]: {
          width: "100%",
          height: "100%",
        },
      },
    },
    [groupLeftColumnHeaderClassName.toClassName()]: {
      backgroundColor: theme.palette.common.white,
      borderLeft: `1px solid ${theme.palette.grey[100]} !important`,
      fontFamily: theme.typography.body1Mono.fontFamily,
      fontWeight: `${String(theme.typography.fontWeightBold)} !important`,
      width: "100%",
      [".MuiDataGrid-columnHeaderTitleContainer"]: {
        width: "100%",
        [".MuiDataGrid-columnHeaderTitleContainerContent"]: {
          width: "100%",
          height: "100%",
        },
      },
    },
    [".MuiDataGrid-columnHeaderTitle"]: {
      fontSize: theme.typography.body1.fontSize,
    },
    [".MuiDataGrid-cell"]: {
      fontFamily: theme.typography.body1Mono.fontFamily,
    },
    [".MuiDataGrid-row.total-row"]: {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    [".MuiDataGrid-row.total-row-final"]: {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    [".MuiDataGrid-row.collapsed-row"]: {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    [`.MuiDataGrid-row.collapsed-row ${mixSummaryCellClassName.toClassName()} .MuiStack-root`]:
      {
        gap: theme.spacing(0.25),
      },
    ["input::-webkit-outer-spin-button, input::-webkit-inner-spin-button"]: {
      "-webkit-appearance": "none",
    },
    [".MuiDataGrid-columnHeader--filtered .MuiDataGrid-filterIcon"]: {
      display: "none",
    },
    [".MuiDataGrid-columnHeader"]: {
      textAlign: "center",
      padding: 0,
    },
    [".MuiDataGrid-bottomContainer"]: {
      display: "none",
    },
  };
};
